import { Link } from 'react-router-dom';
import { site } from '@shared/site.config';
import { useTitle } from '@/lib/useTitle';
import DimensionLine from '@/components/DimensionLine';

/**
 * Sequenced like a construction schedule: three phases, each with what happens
 * and who's holding it. Nothing here claims a timeline in weeks — that would be
 * a number I don't have.
 */
const PHASES = [
  {
    step: '01',
    title: 'A direct conversation',
    body: 'What you’re trying to do, what’s driving the timeline, and whether it’s actually a fit. No intake form, no hand-off to someone else first.',
  },
  {
    step: '02',
    title: 'A plan built on your numbers',
    body: 'Pricing, positioning or search criteria based on what’s actually comparable — not a generic market report.',
  },
  {
    step: '03',
    title: 'Through to closing, personally',
    body: 'Offers, due diligence, and the back-and-forth with your lawyer and lender. Same phone number the whole way through.',
  },
];

export default function About() {
  useTitle('About', site.brand.description);

  return (
    <>
      <section className="relative border-b border-medline bg-grid-sheet">
        <span className="sheet-no absolute right-5 top-5 sm:right-8">A-04</span>

        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 sm:py-24">
          <div className="animate-fade-in max-w-3xl">
            <p className="annot">About</p>
            <h1 className="mt-4 font-display text-[42px] font-semibold leading-[1.0] tracking-tight sm:text-[62px]">
              One agent, four cities,
              <br />
              no hand-offs.
            </h1>

            <div className="mt-8 space-y-5 text-[17px] leading-relaxed text-graphite-600">
              {site.agent.bio.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
              <p>
                Residential buyers and sellers across the same four cities are looked after through
                Fourcities, the residential sister site — kept separate because commercial deals move
                differently, but it’s the same brokerage and the same direct line either way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials, as a data block rather than a paragraph. */}
      <section className="bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16">
          {/* Registration is omitted entirely until the real number is known —
              the cell count follows, so the block never shows a gap. */}
          <div
            className={`grid grid-cols-1 gap-px border border-medline bg-medline ${
              site.agent.registration ? 'sm:grid-cols-3' : 'sm:grid-cols-2'
            }`}
          >
            {[
              { label: 'Salesperson', value: site.agent.name, sub: site.agent.title },
              { label: 'Brokerage', value: site.agent.brokerage, sub: site.agent.brokerageCity },
              ...(site.agent.registration
                ? [{ label: 'Registration', value: site.agent.registration, sub: 'Real Estate Council of Ontario' }]
                : []),
            ].map((row) => (
              <div key={row.label} className="bg-sheet px-6 py-6">
                <p className="annot">{row.label}</p>
                <p className="mt-2 font-display text-[19px] leading-snug">{row.value}</p>
                <p className="mt-1 text-[13px] text-annot">{row.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Phases — a vertical sequence with a drawn spine, not three cards. */}
      <section className="border-t border-medline">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <DimensionLine label="Sequence — how it goes" className="mb-14" />

          <div className="max-w-4xl">
            {PHASES.map((p) => (
              <div
                key={p.step}
                className="grid grid-cols-[auto_1fr] gap-x-8 border-t border-medline py-9 last:border-b last:border-medline"
              >
                <span className="font-display text-[34px] leading-none text-medline sm:text-[44px]">{p.step}</span>
                <div>
                  <h2 className="font-display text-[24px] leading-tight sm:text-[28px]">{p.title}</h2>
                  <p className="mt-3 max-w-2xl text-[16px] leading-relaxed text-annot">{p.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-medline bg-sheet">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20">
          <div className="flex flex-wrap items-end justify-between gap-8">
            <div className="max-w-xl">
              <h2 className="font-display text-[32px] leading-tight sm:text-[40px]">Start with a conversation</h2>
              <p className="mt-4 text-[16px] leading-relaxed text-annot">
                Buying, selling, leasing, or just trying to work out whether now is the moment. No obligation.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/contact" className="btn-primary">Send a message</Link>
              <a href={`tel:${site.agent.phone.replace(/\D/g, '')}`} className="btn-ghost">
                Call {site.agent.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
